import { createModel } from '@rematch/core';
import { RootModel } from './RootState';
import { Cart } from '../models/Cart';
import { getConnector } from '../services/Connector';
import { addInteraction } from '../services/ActiveHelpService';
import { Product } from '../models/Product';
import { LoadingStatus } from '../models/LoadingStatus';
import { dispatchEvent } from '../services/EventService';

export type CartState = {
    currentCart: Cart;
    loadingStatus: LoadingStatus;
    isInitialized: boolean;
    isAdding: boolean;
    addingLoadingStatus: LoadingStatus;
    addingProductIds: string[];
    addingSelectedVariantIds?: string[];
    addingToCartStatus: LoadingStatus;
};

const initialState: CartState = {
    currentCart: {
        currency: 'EUR',
        lines: [],
        note: '',
        originalTotalPrice: 0,
        token: '',
        totalDiscount: 0,
        totalPrice: 0,
        totalQuantity: 0
    },
    loadingStatus: 'idle',
    isInitialized: false,
    isAdding: false,
    addingLoadingStatus: 'idle',
    addingProductIds: [],
    addingToCartStatus: 'idle'
};

export const cartState = createModel<RootModel>()({
    state: initialState,
    reducers: {
        setCurrentCart(state, currentCart: Cart) {
            return { ...state, currentCart };
        },
        setLoadingStatus(state, loadingStatus: LoadingStatus) {
            return { ...state, loadingStatus };
        },
        markAsInitialized(state) {
            return { ...state, isInitialized: true };
        },
        setAddingState(
            state,
            props: Partial<
                Pick<
                    CartState,
                    | 'isAdding'
                    | 'addingLoadingStatus'
                    | 'addingSelectedVariantIds'
                    | 'addingToCartStatus'
                >
            >
        ) {
            return {
                ...state,
                ...props
            };
        },
        reset(state) {
            return {
                ...state,
                isAdding: false,
                addingLoadingStatus: 'idle',
                addingProductIds: [],
                addingProducts: undefined,
                addingSelectedVariantIds: undefined,
                addingToCartStatus: 'idle'
            };
        }
    },
    effects: dispatch => ({
        async reloadCurrentCart(_: void, state) {
            dispatch.cartState.setLoadingStatus('loading');
            const previousCart = state.cartState.currentCart;
            const newCart = await getConnector().getCurrentCart();
            if (state.cartState.isInitialized) {
                for (const newCartLine of newCart.lines) {
                    const previousCartLine = previousCart.lines.find(
                        l => l.variantId === newCartLine.variantId
                    );
                    if (
                        !previousCartLine ||
                        previousCartLine.quantity < newCartLine.quantity
                    ) {
                        await addInteraction({
                            type: 'cart-line-addition',
                            date: new Date(),
                            newCart,
                            previousCart,
                            newCartLine,
                            previousCartLine: previousCartLine ?? null
                        });
                    }
                    // TODO: Check here the cart-line-substraction
                }
            }
            dispatch.cartState.setCurrentCart(newCart);
            dispatch.cartState.setLoadingStatus('idle');
            dispatch.cartState.markAsInitialized();
        },
        async addVariantToCart(data: {
            product: Product;
            variantId: string;
            quantity: number;
        }) {
            const { product, variantId, quantity } = data;
            dispatch.cartState.setLoadingStatus('loading');

            try {
                await addToCart(product.productId, variantId, quantity);

                dispatch.featureState.appendProductAddedToCartMessage({
                    product,
                    variantId
                });
            } catch (error) {
                dispatch.featureState.appendAddToCartErrorMessage();
            } finally {
                dispatch.cartState.setLoadingStatus('idle');
            }
        },
        async addMultiVariantToCart(
            selectedVariants: {
                productId: string;
                variantId: string;
            }[]
        ) {
            dispatch.cartState.setAddingState({
                addingToCartStatus: 'loading'
            });
            try {
                for (const { productId, variantId } of selectedVariants) {
                    await addToCart(productId, variantId);
                }
                dispatch.cartState.setAddingState({
                    addingToCartStatus: 'success'
                });
            } catch {
                dispatch.cartState.setAddingState({
                    addingToCartStatus: 'error'
                });
            } finally {
                dispatch.cartState.setLoadingStatus('idle');
            }
        }
    })
});

async function addToCart(productId: string, variantId: string, quantity = 1) {
    await getConnector().addProductVariantToCart({
        quantity,
        productId,
        variantId
    });
    dispatchEvent({
        type: 'bri.click_add_product_to_cart',
        isInteraction: true,
        productId,
        variantId
    });
}
